import { CampaignGreeting } from './types';

/**
 * The greeting as ONE message body, title included.
 *
 * The opened panel renders `Message.text` and nothing else — the message model has no title slot,
 * and `addResponseMessage(text)` has no second field to put one in. So a reveal that passed only
 * `greeting.body` silently DROPPED the title: the teaser showed "Need a hand with pricing?" above
 * the message, the visitor tapped it, and the question they had just been asked was gone from the
 * thread they landed in.
 *
 * Joining the two with a blank line is the honest fix at this contract: `.rcw-message-text` is
 * `white-space: pre-wrap`, so the break renders, and the title reads as its own line above the
 * body exactly as the mockup's `.gmsg > strong` draws it. Bolding it would need a rich-text
 * message model that does not exist; inventing one to carry a heading is a larger change than the
 * defect warrants and would fork the message shape for one caller.
 *
 * A greeting whose title is missing or duplicates the body renders the body alone rather than the
 * same sentence twice.
 */
export const greetingMessageText = (greeting: Pick<CampaignGreeting, 'title' | 'body'>): string => {
  const title = greeting.title?.trim() ?? '';
  const body = greeting.body ?? '';
  if (!title || title === body.trim()) return body;
  return `${title}\n\n${body}`;
};

/**
 * The initial drawn in the avatar circle when the sender has no photo. `sender.name` is OPTIONAL
 * (PRD AC-1.1) — an unnamed greeting drops the avatar and the name row together, so the caller
 * only reaches this with a non-empty name and it is never asked for the initial of "".
 */
export const senderInitial = (senderName: string): string => (senderName.trim()[0] ?? '').toUpperCase();
